import { ProfileTopBar } from "./profile-top-bar";
import { ProfileSummary } from "./profile-summary";
import { ProfileStatCard } from "./profile-stat-card";
import { ProfileInfoList } from "./profile-info-list";
import { ProfileContactGrid } from "./profile-contact-grid";
import {
  CalendarDays,
  Dumbbell,
  Headphones,
  HeartPulse,
  Instagram,
  PersonStanding,
  Send,
  Trophy,
} from "lucide-react";
import { getUserAge, getYearWord } from "./profile-formatters";
import type { UserFullInfo } from "@entities/User/types/user.fullInfo";

interface ProfileHomeProps {
  avatarUrl?: string;
  userData: Partial<UserFullInfo>;
  onEditClick: () => void;
  onInfoClick: () => void;
  onInfoPanelClick: (type: string) => void;
}

export const ProfileHome = ({
  avatarUrl,
  userData,
  onEditClick,
  onInfoClick,
  onInfoPanelClick,
}: ProfileHomeProps) => {
  const age = getUserAge(userData.birthday);
  const trainingYears = userData.yearsExperience || null;

  const infoItems = [
    {
      accentClassName: "bg-[#EFF5FF]",
      icon: (
        <PersonStanding className="size-11" color="#2F6DFF" strokeWidth={2.2} />
      ),
      title: "Данные о теле",
      type: "body",
    },
    {
      accentClassName: "bg-[#FFF0F2]",
      icon: <HeartPulse className="size-8" color="#FF4057" strokeWidth={2.2} />,
      title: "Данные о здоровье",
      type: "health",
    },
    {
      accentClassName: "bg-[#F6FCE4]",
      icon: <Trophy className="size-8" color="#C4E700" strokeWidth={2.2} />,
      title: "Цели атлета",
      type: "targets",
    },
  ];

  const contactItems = [
    {
      href: "https://t.me/tisit_club",
      icon: (
        <Send
          className="size-[18px] mr-[1px] mt-[1px]"
          color="#2F6DFF"
          fill="#2F6DFF"
          strokeWidth={2.2}
        />
      ),
      iconClassName: "bg-[#F1F6FF]",
      title: "Telegram",
    },
    {
      href: "https://www.instagram.com/tisit.club/",
      icon: (
        <Instagram className="size-[20px]" color="#FF4E93" strokeWidth={2.2} />
      ),
      iconClassName: "bg-[#FFF0F7]",
      title: "Instagram",
    },
    {
      href: "https://vk.com/tisit.club",
      icon: (
        <svg
          className="size-[42px]"
          viewBox="0 0 24 24"
          fill="none"
          aria-hidden="true"
        >
          <path
            d="M13.05 17.4c-5.2 0-8.16-3.57-8.28-9.5h2.62c.09 4.35 2 6.2 3.5 6.58V7.9h2.47v3.76c1.48-.16 3.02-1.88 3.54-3.76h2.47a7.25 7.25 0 0 1-3.27 4.73 7.5 7.5 0 0 1 3.83 4.77h-2.72c-.57-1.8-1.97-3.2-3.85-3.39v3.39Z"
            fill="#2F8CFF"
          />
        </svg>
      ),
      iconClassName: "bg-[#EFF5FF]",
      title: "ВКонтакте",
    },
    {
      href: "https://t.me/artiligas",
      icon: (
        <Headphones className="size-[20px]" color="#55585C" strokeWidth={2.2} />
      ),
      iconClassName: "bg-[#F4F5F3]",
      title: "Поддержка",
    },
  ];

  return (
    <div className="mx-auto flex h-full w-full max-w-[500px] flex-col overflow-hidden bg-[#FEFEFC]">
      <div className="relative z-20 shrink-0 bg-[#FEFEFC] pb-4">
        <ProfileTopBar onInfoClick={onInfoClick} />
        <div className="-mt-5 px-5">
          <ProfileSummary
            avatarUrl={avatarUrl}
            userData={userData}
            onEditClick={onEditClick}
          />
        </div>
        <span
          aria-hidden="true"
          className="pointer-events-none absolute inset-x-0 bottom-[-44px] h-11 bg-gradient-to-b from-[#FEFEFC] via-[#FEFEFC]/90 to-[#FEFEFC]/0"
        />
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-5 pb-[var(--app-bottom-nav-reserve)] pt-8 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        <section className="grid grid-cols-2 gap-4">
          <ProfileStatCard
            icon={
              <CalendarDays
                className="size-[22px]"
                color="#A5A7AA"
                strokeWidth={2}
              />
            }
            label="Возраст"
            value={age}
            suffix={age ? getYearWord(age) : ""}
          />
          <ProfileStatCard
            icon={
              <Dumbbell
                className="size-[22px]"
                color="#A5A7AA"
                strokeWidth={2}
              />
            }
            label="В клубе"
            value={trainingYears}
            suffix={trainingYears ? getYearWord(trainingYears) : ""}
          />
        </section>

        <ProfileInfoList items={infoItems} onSelect={onInfoPanelClick} />
        <ProfileContactGrid items={contactItems} />
      </div>
    </div>
  );
};
